import { ErrorMessage as ErrorCode } from '../Codes';

export enum ErrorType {
    None = 'None',
    General = 'General',
    AcceptTerms = 'AcceptTerms',
    Email = 'Email',
    FirstName = 'FirstName',
    LastName = 'LastName',
    Password = 'Password',
    PracticeName = 'PracticeName',
}

export class SignupErrorCode {
    static PasswordTooShort = 'PASSWORD_TOO_SHORT';
    static PasswordMissingUpperAndLowerCase = 'PASSWORD_MISSING_UPPER_AND_LOWER_CASE';
    static PasswordMissingNumber = 'PASSWORD_MISSING_NUMBER';
    static GeneralError = 'GENERAL_ERROR';
}

export const ErrorMessages = {
    [ErrorCode.EmailAddressInUse]:
        'This email address is already in use. Please use a different email address or contact <a class="signup-link contact-support-link" href="mailto:support@spotlightreporting.com">support@spotlightreporting.com</a> for assistance.',
    [ErrorCode.InvalidEmailAddress]: 'Please enter a valid email format, (e.g. name@example.com)',
    [ErrorCode.PracticeNameRequired]: 'Please enter your company name',
    [ErrorCode.PracticeNameTooLong]: 'Please enter a shorter company name',
    [SignupErrorCode.PasswordTooShort]: 'Please use at least eight characters',
    [SignupErrorCode.PasswordMissingUpperAndLowerCase]: 'Please use upper and lower case letters (e.g. Aa)',
    [SignupErrorCode.PasswordMissingNumber]: 'Please include at least one number',
    [ErrorCode.WeakPassword]:
        'Your password must contain a minimum of eight characters, including at least one number, one upper and one lower case letter (e.g. 123456Aa)',
    [ErrorCode.InvalidTrialProfile]: 'Please select the plan you want to trial',
    [ErrorCode.AcceptTerms]: 'Please read and accept our legal terms to continue',
    [SignupErrorCode.GeneralError]:
        'Sorry, there was an issue processing your signup. Please contact our <a class="signup-link contact-support-link" href="mailto:support@spotlightreporting.com">support@spotlightreporting.com</a> for further assistance.',
    [ErrorCode.CaptchaVerificationFailed]:
        'We couldn\'t verify your request. Please refresh and try again. If this keeps happening, try disabling privacy extensions or switching browsers, or contact <a class="signup-link contact-support-link" href="mailto:support@spotlightreporting.com">support@spotlightreporting.com</a> for further assistance.',
    [ErrorCode.EmailDomainNotSupported]:
        'Please provide a business email address. Disposable or temporary email services aren\'t supported for trial accounts. Contact <a class="signup-link contact-support-link" href="mailto:support@spotlightreporting.com">support@spotlightreporting.com</a> if you believe this is an error.',
};

export const GetErrorTypeByCode = (code: string): string => {
    switch (code) {
        case ErrorCode.AcceptTerms:
            return ErrorType.AcceptTerms;
        case ErrorCode.EmailAddressInUse:
        case ErrorCode.InvalidEmailAddress:
            return ErrorType.Email;
        case ErrorCode.PracticeNameRequired:
        case ErrorCode.PracticeNameTooLong:
        case ErrorCode.InvalidOrganisationName:
            return ErrorType.PracticeName;
        case ErrorCode.PasswordRequired:
        case SignupErrorCode.PasswordTooShort:
        case SignupErrorCode.PasswordMissingUpperAndLowerCase:
        case ErrorCode.WeakPassword:
            return ErrorType.Password;
        case ErrorCode.FirstNameRequired:
            return ErrorType.FirstName;
        case ErrorCode.LastNameRequired:
            return ErrorType.LastName;
        case ErrorCode.InvalidTrialProfile:
        case ErrorCode.ServerError:
            return ErrorType.General;
        default:
            return ErrorType.General;
    }
};

export const ClearErrorMessages = (element: Element): void => {
    const errorMessageCollection: NodeList = element.querySelectorAll('.error-text');
    const errorMessages = errorMessageCollection ? Array.from(errorMessageCollection) : [];

    errorMessages.forEach((error: any) => {
        error.remove();
    });
};

export const ClearInfoMessages = (element: Element): void => {
    const infoMessageCollection = element.querySelectorAll('.info-text');
    const infoMessages = infoMessageCollection ? Array.from(infoMessageCollection) : [];

    infoMessages.forEach((info: any) => {
        info.remove();
    });
};

export const CreatePasswordValidationMessageHTML = (isError: boolean, message: string): HTMLDivElement => {
    const icon = isError
        ? '<i class="fa fa-times" aria-hidden="true"></i>'
        : '<i class="fa fa-check" aria-hidden="true"></i>';

    const style = isError ? 'error-text' : 'info-text';

    const messageHTML = document.createElement('div');
    messageHTML.classList.add(style);
    messageHTML.innerHTML = icon + message;

    return messageHTML;
};

export const CreateErrorMessageHTML = (message: string): HTMLDivElement => {
    const messageHTML = document.createElement('div');
    messageHTML.classList.add('error-text');
    messageHTML.insertAdjacentHTML('beforeend', message);

    return messageHTML;
};

export const ContainerHasNoError = (container: Element): boolean => {
    return container.querySelector('.error-text') ? false : true;
};

export const FocusOnFirstInputWithError = (): void => {
    const formEl = document.getElementById('SignupForm');
    const firstErrorEl = formEl.querySelector('.error-text');
    if (firstErrorEl) {
        const inputEl = firstErrorEl.parentElement.querySelector('input');
        inputEl && inputEl.focus();
    }
};
